import { streamingRemoteFeedback, streamingResponseText, streamingFieldText, streamingFeedbackText } from "../i18n/streamingFeedbackPresentation";
import { type StreamingMessage, streamText, streamLocal, useStreamingLanguage, streamMessage } from "../i18n/streamingDashboardPresentation";
/**
 * useServerProxy -- Hooks for Control Center server proxy endpoints.
 *
 * Each hook calls a GET /api/streaming/* endpoint that proxies to
 * the upstream Emby/Jellyfin API and returns normalized read-only data.
 *
 * Hooks:
 * - useServerTasks()          -- Scheduled tasks (5.1-5.2)
 * - useActivityLog()          -- Activity log (1.5)
 * - useServerPlugins()        -- Installed plugins (6.9)
 * - useServerPing()           -- Health check / ping (1.3)
 * - useServerConfiguration()  -- Server configuration (6.6-6.7)
 * - useServerStorage()        -- Storage volumes (1.4, Jellyfin-only)
 * - useServerLogs()           -- Server log files (1.6)
 * - useServerEncoding()       -- Encoding capabilities (1.7, Emby-only)
 * - useReleaseNotes()         -- Release notes (1.13, Emby-only)
 * - useGeoStats()             -- GeoIP cache/service stats (5.7)
 * - useContentSearch()        -- Content item search (3.9)
 * - usePhysicalPaths()        -- Physical filesystem paths (3.10, Emby-only)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getCsrfToken } from "../lib/csrf";

// ============================================================================
// Shared Types
// ============================================================================

/** Per-server wrapper used by all proxy responses */
interface ServerResult<T> {
  serverType: string;
  error?: string | null;
  [key: string]: unknown;
  // Data varies by endpoint — typed per hook
}

/** Standard result shape for mutation calls */
export type MutateResult<T = unknown> =
  | { ok: true; data: T }
  | { ok: false; message: string; presentation?: StreamingMessage };

// ============================================================================
// Shared Fetch Helper (read-only GET requests)
// ============================================================================

async function fetchProxy<T>(
  path: string,
  params?: Record<string, string | number | undefined>,
): Promise<{ ok: true; data: T } | { ok: false; message: string; presentation?: StreamingMessage }> {
  try {
    const url = new URL(path, window.location.origin);
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      }
    }

    const res = await fetch(url.toString(), {
      headers: { Accept: "application/json" },
    });

    const json = await res.json();

    if (!res.ok || !json.ok) {
      return { ok: false, message: json.message ?? `HTTP ${res.status}`, presentation: streamingRemoteFeedback(json) };
    }

    return { ok: true, data: json.data as T };
  } catch {
    return { ok: false, message: streamText("networkError"), presentation: streamLocal("networkError") };
  }
}

// ============================================================================
// Mutation Fetch Helper (POST/PATCH/DELETE with CSRF)
// ============================================================================

export async function mutateProxy<T = unknown>(
  method: "POST" | "PATCH" | "DELETE" | "PUT",
  path: string,
  body?: Record<string, unknown>,
): Promise<MutateResult<T>> {
  try {
    const csrfToken = getCsrfToken();
    const url = new URL(path, window.location.origin);

    const hasBody = body !== undefined && body !== null;
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
    };
    if (hasBody) {
      headers["Content-Type"] = "application/json";
    }

    const res = await fetch(url.toString(), {
      method,
      headers,
      body: hasBody ? JSON.stringify(body) : undefined,
    });

    const json = await res.json();

    if (!res.ok || !json.ok) {
      return { ok: false, message: json.message ?? `HTTP ${res.status}`, presentation: streamingRemoteFeedback(json) };
    }

    return { ok: true, data: json.data as T };
  } catch {
    return { ok: false, message: streamText("networkError"), presentation: streamLocal("networkError") };
  }
}

// ============================================================================
// useServerTasks -- Scheduled Tasks (5.1-5.2 / Intent 6.17)
// ============================================================================

export interface TaskTrigger {
  type: string | null;
  timeOfDayTicks: number | null;
  intervalTicks: number | null;
  dayOfWeek: string | null;
  maxRuntimeTicks: number | null;
}

export interface TaskLastResult {
  status: string | null;
  startTime: string | null;
  endTime: string | null;
  errorMessage: string | null;
}

export interface ScheduledTask {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  state: "Idle" | "Running" | "Cancelling";
  currentProgress: number | null;
  isHidden: boolean;
  lastResult: TaskLastResult | null;
  triggers: TaskTrigger[];
}

export interface ServerTasks {
  serverType: string;
  /** false for servers that have no scheduled-tasks endpoint (e.g. Plex) */
  supported: boolean;
  tasks: ScheduledTask[];
}

interface TasksResponse {
  servers: Array<{ serverType: string; tasks: ScheduledTask[] }>;
}

/** Refresh interval: 30 seconds (tasks change state during runs) */
const TASKS_REFRESH_MS = 30_000;

export const useServerTasks = (enabled: boolean = true, plexConfigured: boolean = false) => {
  const [data, setData] = useState<ServerTasks[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);
  const intervalRef = useRef<number | null>(null);
  const initialRef = useRef(true);

  const fetch_ = useCallback(async () => {
    if (initialRef.current) setLoading(true);
    setError(null);

    const result = await fetchProxy<TasksResponse>("/api/streaming/server/tasks");
    if (result.ok) {
      // Normalize backend entries to include supported: true
      const normalized: ServerTasks[] = result.data.servers.map((s) => ({
        ...s,
        supported: true,
      }));
      // Inject unsupported Plex entry if Plex is configured but backend returned no Plex row
      if (plexConfigured && !normalized.some((s) => s.serverType === "plex")) {
        normalized.push({ serverType: "plex", supported: false, tasks: [] });
      }
      setData(normalized);
    } else {
      setError(result.presentation ?? result.message);
    }

    setLoading(false);
    initialRef.current = false;
  }, [plexConfigured]);

  useEffect(() => {
    if (!enabled) {
      setData([]);
      return;
    }
    void fetch_();
    intervalRef.current = window.setInterval(() => void fetch_(), TASKS_REFRESH_MS);
    return () => {
      if (intervalRef.current !== null) window.clearInterval(intervalRef.current);
    };
  }, [enabled, fetch_]);

  return { servers: data, loading, error, refetch: fetch_ };
};

// ============================================================================
// useActivityLog -- Activity Log (1.5)
// ============================================================================

export interface ActivityLogEntry {
  id: number;
  name: string;
  overview: string | null;
  shortOverview: string | null;
  type: string | null;
  severity: string;
  date: string;
  userId: string | null;
}

export interface ServerActivityLog {
  serverType: string;
  entries: ActivityLogEntry[];
  totalCount: number;
  error?: string;
}

interface ActivityLogResponse {
  servers: ServerActivityLog[];
  limit: number;
  startIndex: number;
}

export const useActivityLog = (
  enabled: boolean = true,
  options?: { limit?: number; startIndex?: number; minDate?: string },
) => {
  const [data, setData] = useState<ServerActivityLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);

  const limit = options?.limit ?? 50;
  const startIndex = options?.startIndex ?? 0;
  const minDate = options?.minDate;

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await fetchProxy<ActivityLogResponse>(
      "/api/streaming/server/activity-log",
      { limit, startIndex, minDate },
    );

    if (result.ok) {
      setData(result.data.servers);
    } else {
      setError(result.presentation ?? result.message);
    }

    setLoading(false);
  }, [limit, startIndex, minDate]);

  useEffect(() => {
    if (!enabled) {
      setData([]);
      return;
    }
    void fetch_();
  }, [enabled, fetch_]);

  return { servers: data, loading, error, refetch: fetch_ };
};

// ============================================================================
// useServerPlugins -- Installed Plugins (6.9)
// ============================================================================

export interface PluginInfo {
  id: string;
  name: string;
  version: string | null;
  description: string | null;
  status: string | null;
  canUninstall: boolean;
  hasImage: boolean;
  hasConfiguration: boolean;
}

export interface ServerPlugins {
  serverType: string;
  /** false for servers that have no plugin system (e.g. Plex) */
  supported: boolean;
  plugins: PluginInfo[];
}

interface PluginsResponse {
  servers: Array<{ serverType: string; plugins: PluginInfo[] }>;
}

export const useServerPlugins = (enabled: boolean = true, plexConfigured: boolean = false) => {
  const [data, setData] = useState<ServerPlugins[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await fetchProxy<PluginsResponse>("/api/streaming/server/plugins");
    if (result.ok) {
      // Normalize backend entries to include supported: true
      const normalized: ServerPlugins[] = result.data.servers.map((s) => ({
        ...s,
        supported: true,
      }));
      // Inject unsupported Plex entry if Plex is configured but backend returned no Plex row
      if (plexConfigured && !normalized.some((s) => s.serverType === "plex")) {
        normalized.push({ serverType: "plex", supported: false, plugins: [] });
      }
      setData(normalized);
    } else {
      setError(result.presentation ?? result.message);
    }

    setLoading(false);
  }, [plexConfigured]);

  useEffect(() => {
    if (!enabled) {
      setData([]);
      return;
    }
    void fetch_();
  }, [enabled, fetch_]);

  return { servers: data, loading, error, refetch: fetch_ };
};

// ============================================================================
// useServerPing -- Health Check / Ping (1.3)
// ============================================================================

export interface ServerPingResult {
  serverType: string;
  ok: boolean;
  latencyMs: number;
  serverName: string | null;
  version: string | null;
  operatingSystem: string | null;
  architecture: string | null;
  hasUpdateAvailable: boolean | null;
  canSelfUpdate: boolean | null;
  error: string | null;
}

interface PingResponse {
  servers: ServerPingResult[];
}

export const useServerPing = (enabled: boolean = true) => {
  const [data, setData] = useState<ServerPingResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await fetchProxy<PingResponse>("/api/streaming/server/ping");
    if (result.ok) {
      setData(result.data.servers);
    } else {
      setError(result.presentation ?? result.message);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setData([]);
      return;
    }
    void fetch_();
  }, [enabled, fetch_]);

  return { servers: data, loading, error, refetch: fetch_ };
};

// ============================================================================
// useServerConfiguration -- Server Configuration Viewer (6.6-6.7)
// ============================================================================

export interface ServerConfigResult {
  serverType: string;
  section: string | null;
  configuration: Record<string, unknown> | null;
  error: string | null;
}

interface ConfigResponse {
  servers: ServerConfigResult[];
}

export const useServerConfiguration = (
  enabled: boolean = true,
  section?: string,
) => {
  const [data, setData] = useState<ServerConfigResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await fetchProxy<ConfigResponse>(
      "/api/streaming/server/configuration",
      section ? { section } : undefined,
    );

    if (result.ok) {
      setData(result.data.servers);
    } else {
      setError(result.presentation ?? result.message);
    }

    setLoading(false);
  }, [section]);

  useEffect(() => {
    if (!enabled) {
      setData([]);
      return;
    }
    void fetch_();
  }, [enabled, fetch_]);

  return { servers: data, loading, error, refetch: fetch_ };
};

// ============================================================================
// useServerStorage -- Storage Volumes (1.4, Jellyfin-only)
// ============================================================================

export interface StorageVolume {
  name: string;
  path: string;
  totalBytes: number;
  freeBytes: number;
  usedBytes: number;
}

export interface ServerStorageResult {
  serverType: string;
  supported: boolean;
  storage: StorageVolume[] | null;
  error: string | null;
}

interface StorageResponse {
  servers: ServerStorageResult[];
}

export const useServerStorage = (enabled: boolean = true) => {
  const [data, setData] = useState<ServerStorageResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await fetchProxy<StorageResponse>("/api/streaming/server/storage");
    if (result.ok) {
      setData(result.data.servers);
    } else {
      setError(result.presentation ?? result.message);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setData([]);
      return;
    }
    void fetch_();
  }, [enabled, fetch_]);

  return { servers: data, loading, error, refetch: fetch_ };
};

// ============================================================================
// useServerLogs -- Server Log Files (1.6)
// ============================================================================

export interface ServerLogFile {
  name: string;
  dateCreated: string | null;
  dateModified: string | null;
  size: number;
}

export interface ServerLogsResult {
  serverType: string;
  logs: ServerLogFile[];
}

interface LogsResponse {
  servers: ServerLogsResult[];
}

export const useServerLogs = (enabled: boolean = true) => {
  const [data, setData] = useState<ServerLogsResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await fetchProxy<LogsResponse>("/api/streaming/server/logs");
    if (result.ok) {
      setData(result.data.servers);
    } else {
      setError(result.presentation ?? result.message);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setData([]);
      return;
    }
    void fetch_();
  }, [enabled, fetch_]);

  return { servers: data, loading, error, refetch: fetch_ };
};

// ============================================================================
// useServerEncoding -- Encoding Capabilities (1.7, Emby-only)
// ============================================================================

export interface ServerEncodingResult {
  serverType: string;
  supported: boolean;
  capabilities: Record<string, unknown> | null;
  error: string | null;
}

interface EncodingResponse {
  servers: ServerEncodingResult[];
}

export const useServerEncoding = (enabled: boolean = true) => {
  const [data, setData] = useState<ServerEncodingResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await fetchProxy<EncodingResponse>("/api/streaming/server/encoding");
    if (result.ok) {
      setData(result.data.servers);
    } else {
      setError(result.presentation ?? result.message);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setData([]);
      return;
    }
    void fetch_();
  }, [enabled, fetch_]);

  return { servers: data, loading, error, refetch: fetch_ };
};

// ============================================================================
// useReleaseNotes -- Release Notes (1.13, GitHub-sourced for both servers)
// ============================================================================

export interface ServerReleaseNotesResult {
  serverType: string;
  supported: boolean;
  releases: unknown[] | null;
  error: string | null;
}

interface ReleaseNotesResponse {
  servers: ServerReleaseNotesResult[];
}

export const useReleaseNotes = (enabled: boolean = true) => {
  const [data, setData] = useState<ServerReleaseNotesResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await fetchProxy<ReleaseNotesResponse>("/api/streaming/server/release-notes");
    if (result.ok) {
      setData(result.data.servers);
    } else {
      setError(result.presentation ?? result.message);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setData([]);
      return;
    }
    void fetch_();
  }, [enabled, fetch_]);

  return { servers: data, loading, error, refetch: fetch_ };
};

// ============================================================================
// useGeoStats -- GeoIP Service & Cache Stats (5.7)
// ============================================================================

export interface GeoDbCacheStats {
  totalRecords: number;
  successCount: number;
  notFoundCount: number;
  privateIpCount: number;
  errorCount: number;
  rateLimitedCount: number;
  pendingCount: number;
}

export interface GeoServiceStats {
  memoryCacheSize: number;
  rateLimited: boolean;
  backoffUntil: number | null;
  requestsThisWindow: number;
  dbCacheStats: GeoDbCacheStats;
}

/**
 * NOTE: The geo/stats endpoint returns `{ ok, service, cache }` at the top level,
 * NOT wrapped in `data`. We use a raw fetch instead of fetchProxy.
 */
export const useGeoStats = (enabled: boolean = true) => {
  const [data, setData] = useState<GeoServiceStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/streaming/geo/stats", {
        headers: { Accept: "application/json" },
      });
      const json = await res.json();

      if (!res.ok || !json.ok) {
        setError((streamingRemoteFeedback(json) ?? json.message ?? `HTTP ${res.status}`));
      } else {
        setData(json.service as GeoServiceStats);
      }
    } catch {
      setError(streamLocal("networkError"));
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      return;
    }
    void fetch_();
  }, [enabled, fetch_]);

  return { data, loading, error, refetch: fetch_ };
};

// ============================================================================
// useContentSearch -- Content Item Search (3.9)
// ============================================================================

export interface ContentSearchItem {
  id: string;
  name: string;
  type: string;
  seriesName: string | null;
  seasonNumber: number | null;
  episodeNumber: number | null;
  productionYear: number | null;
  overview: string | null;
  imageTag: string | null;
  backdropTag: string | null;
  genres: string[];
  officialRating: string | null;
  communityRating: number | null;
  runTimeTicks: number | null;
  quality: string | null;
  videoType: string | null;
  // Enriched metadata
  dateCreated: string | null;
  premiereDate: string | null;
  studios: string[];
  tagline: string | null;
  container: string | null;
  fileSize: number | null;
  bitrate: number | null;
  videoCodec: string | null;
  videoWidth: number | null;
  audioCodec: string | null;
  audioChannels: number | null;
  audioChannelLayout: string | null;
  hasSubtitles: boolean;
  isHdr: boolean;
  tmdbId: string | null;
  tvdbId: string | null;
}

export interface ServerSearchResult {
  serverType: string;
  items: ContentSearchItem[];
  totalCount: number;
  error?: string;
}

interface ContentSearchResponse {
  servers: ServerSearchResult[];
  searchTerm: string;
  limit: number;
  startIndex: number;
}

/**
 * Search hook with on-demand fetching (not auto-fetch on mount).
 * Returns a `search(query)` function, results, loading, and error state.
 */
export const useContentSearch = () => {
  const [servers, setServers] = useState<ServerSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);

  const search = useCallback(async (
    query: string,
    limit = 25,
    startIndex = 0,
    options?: { parentId?: string; includeItemTypes?: string },
  ) => {
    if (!query.trim()) {
      setServers([]);
      return;
    }

    setLoading(true);
    setError(null);

    const result = await fetchProxy<ContentSearchResponse>(
      "/api/streaming/content/search",
      {
        q: query,
        limit,
        startIndex,
        includeItemTypes: options?.includeItemTypes ?? "Movie,Series,Episode",
        ...(options?.parentId ? { parentId: options.parentId } : {}),
      },
    );

    if (result.ok) {
      setServers(result.data.servers);
    } else {
      setError(result.presentation ?? result.message);
    }

    setLoading(false);
  }, []);

  const clear = useCallback(() => {
    setServers([]);
    setError(null);
  }, []);

  return { servers, loading, error, search, clear };
};

// ============================================================================
// usePhysicalPaths -- Physical Filesystem Paths (3.10, Emby-only)
// ============================================================================

export interface ServerPhysicalPathsResult {
  serverType: string;
  supported: boolean;
  paths: string[] | null;
  error: string | null;
}

interface PhysicalPathsResponse {
  servers: ServerPhysicalPathsResult[];
}

export const usePhysicalPaths = (enabled: boolean = true) => {
  const [data, setData] = useState<ServerPhysicalPathsResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await fetchProxy<PhysicalPathsResponse>("/api/streaming/content/physical-paths");
    if (result.ok) {
      setData(result.data.servers);
    } else {
      setError(result.presentation ?? result.message);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setData([]);
      return;
    }
    void fetch_();
  }, [enabled, fetch_]);

  return { servers: data, loading, error, refetch: fetch_ };
};

// ============================================================================
// useMediaLibraries -- List all libraries across servers (Media Portal)
// ============================================================================

export interface MediaLibraryItem {
  id: string;
  name: string;
  collectionType: string | null;
  serverType: string;
}

interface LibrariesResponse {
  libraries: MediaLibraryItem[];
}

export const useMediaLibraries = () => {
  const [libraries, setLibraries] = useState<MediaLibraryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await fetchProxy<LibrariesResponse>("/api/streaming/content/libraries");
    if (result.ok) {
      setLibraries(result.data.libraries);
    } else {
      setError(result.presentation ?? result.message);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    void fetch_();
  }, [fetch_]);

  return { libraries, loading, error, refetch: fetch_ };
};

// ============================================================================
// useLibraryItems -- Browse items in a specific library (Media Portal)
// ============================================================================

export interface LibraryBrowseItem {
  id: string;
  name: string;
  type: string;
  seriesName: string | null;
  seasonNumber: number | null;
  episodeNumber: number | null;
  productionYear: number | null;
  overview: string | null;
  imageTag: string | null;
  backdropTag: string | null;
  genres: string[];
  officialRating: string | null;
  communityRating: number | null;
  runTimeTicks: number | null;
  quality: string | null;
  videoType: string | null;
  // Enriched metadata
  dateCreated: string | null;
  premiereDate: string | null;
  studios: string[];
  tagline: string | null;
  container: string | null;
  fileSize: number | null;
  bitrate: number | null;
  videoCodec: string | null;
  videoWidth: number | null;
  audioCodec: string | null;
  audioChannels: number | null;
  audioChannelLayout: string | null;
  hasSubtitles: boolean;
  isHdr: boolean;
  tmdbId: string | null;
  tvdbId: string | null;
  /** Per-item watch evidence — present only in analytics drill-down mode. */
  analytics?: ItemAnalyticsEvidence;
}

/** Watch evidence attached to grid items when an analytics mode is active. */
export interface ItemAnalyticsEvidence {
  playCount: number;
  watchTimeSecs: number;
  completionPct: number;
}

interface LibraryItemsResponse {
  serverType: string;
  items: LibraryBrowseItem[];
  totalCount: number;
  limit: number;
  startIndex: number;
  /** True when the response was produced by the analytics drill-down path. */
  analyticsMode?: boolean;
}

export interface LibraryItemsOptions {
  limit?: number;
  startIndex?: number;
  sortBy?: string;
  sortOrder?: string;
  includeItemTypes?: string;
  genres?: string;
  nameStartsWith?: string;
  years?: string;
  isHd?: string;
  is4K?: string;
  hasSubtitles?: string;
  filters?: string;
  /** Analytics drill-down: rank played items by metric (activates analytics mode). */
  analyticsSort?: "plays_desc" | "watch_time_desc";
  /** Analytics drill-down: completion bucket filter (activates analytics mode). */
  completionRange?: "low" | "mid" | "high";
  /** Analytics time window — defaults to "global" (admin-configured range) server-side. */
  window?: string;
}

export const useLibraryItems = (
  libraryId: string | null,
  serverType: string | null,
  options?: LibraryItemsOptions,
) => {
  const [items, setItems] = useState<LibraryBrowseItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [analyticsMode, setAnalyticsMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);

  const fetch_ = useCallback(async () => {
    if (!libraryId || !serverType) {
      setItems([]);
      setTotalCount(0);
      setAnalyticsMode(false);
      return;
    }

    setLoading(true);
    setError(null);

    const result = await fetchProxy<LibraryItemsResponse>(
      `/api/streaming/content/libraries/${encodeURIComponent(libraryId)}/items`,
      {
        serverType,
        limit: options?.limit ?? 20,
        startIndex: options?.startIndex ?? 0,
        ...(options?.sortBy ? { sortBy: options.sortBy } : {}),
        ...(options?.sortOrder ? { sortOrder: options.sortOrder } : {}),
        ...(options?.includeItemTypes ? { includeItemTypes: options.includeItemTypes } : {}),
        ...(options?.genres ? { genres: options.genres } : {}),
        ...(options?.nameStartsWith ? { nameStartsWith: options.nameStartsWith } : {}),
        ...(options?.years ? { years: options.years } : {}),
        ...(options?.isHd ? { isHd: options.isHd } : {}),
        ...(options?.is4K ? { is4K: options.is4K } : {}),
        ...(options?.hasSubtitles ? { hasSubtitles: options.hasSubtitles } : {}),
        ...(options?.filters ? { filters: options.filters } : {}),
        ...(options?.analyticsSort ? { analyticsSort: options.analyticsSort } : {}),
        ...(options?.completionRange ? { completionRange: options.completionRange } : {}),
        ...(options?.window ? { window: options.window } : {}),
      },
    );

    if (result.ok) {
      setItems(result.data.items);
      setTotalCount(result.data.totalCount);
      setAnalyticsMode(result.data.analyticsMode === true);
    } else {
      setError(result.presentation ?? result.message);
    }

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId, serverType, options?.limit, options?.startIndex, options?.sortBy, options?.sortOrder,
      options?.includeItemTypes, options?.genres, options?.nameStartsWith, options?.years,
      options?.isHd, options?.is4K, options?.hasSubtitles, options?.filters,
      options?.analyticsSort, options?.completionRange, options?.window]);

  useEffect(() => {
    void fetch_();
  }, [fetch_]);

  return { items, totalCount, analyticsMode, loading, error, refetch: fetch_ };
};

// ---------------------------------------------------------------------------
// useLibraryGenres -- fetch available genres for a library
// ---------------------------------------------------------------------------

interface LibraryGenresResponse {
  genres: Array<{ name: string; id: string }>;
}

export const useLibraryGenres = (
  libraryId: string | null,
  serverType: string | null,
) => {
  const [genres, setGenres] = useState<Array<{ name: string; id: string }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!libraryId || !serverType) {
      setGenres([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchProxy<LibraryGenresResponse>(
      `/api/streaming/content/libraries/${encodeURIComponent(libraryId)}/genres`,
      { serverType },
    ).then((result) => {
      if (cancelled) return;
      if (result.ok) {
        setGenres(result.data.genres);
      }
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [libraryId, serverType]);

  return { genres, loading };
};

// ============================================================================
// useLibraryStatus -- Library Scan Status & Timestamps (3.21)
// ============================================================================

export interface LibraryStatusEntry {
  libraryId: string;
  name: string;
  collectionType: string | null;
  lastScanTimestamp: string | null;
  scanRunning: boolean;
}

export interface ServerLibraryStatus {
  serverType: string;
  libraries: LibraryStatusEntry[];
}

type LibraryStatusResponse = ServerLibraryStatus[];

/** Refresh interval: 15 seconds (scan status changes during active scans) */
const LIBRARY_STATUS_REFRESH_MS = 15_000;

export const useLibraryStatus = (enabled: boolean = true) => {
  const [data, setData] = useState<ServerLibraryStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);
  const intervalRef = useRef<number | null>(null);
  const initialRef = useRef(true);

  const fetch_ = useCallback(async () => {
    if (initialRef.current) setLoading(true);
    setError(null);

    const result = await fetchProxy<LibraryStatusResponse>("/api/streaming/content/library/status");
    if (result.ok) {
      setData(result.data);
    } else {
      setError(result.presentation ?? result.message);
    }

    setLoading(false);
    initialRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled) {
      setData([]);
      return;
    }
    void fetch_();
    intervalRef.current = window.setInterval(() => void fetch_(), LIBRARY_STATUS_REFRESH_MS);
    return () => {
      if (intervalRef.current !== null) window.clearInterval(intervalRef.current);
    };
  }, [enabled, fetch_]);

  return { servers: data, loading, error, refetch: fetch_ };
};
